/*
 * Tracking Targets - Election Candidate Management - Assign Elections to a Target.
 *
 *
 *
 */

import angular from "angular";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";
import moment from "moment-timezone";

import Assert from "tbAssert";
import MessageQueue from "../../models/MessageQueue";

import ngSanitize from "angular-sanitize";
import ngAria from "angular-aria";
import ngAnimate from "angular-animate";
import uiBootstrap from "angular-ui-bootstrap";
import kendoDirectivesModule from "../../shims/kendoDirectives";

import PriorFederalElectionDateServiceModule from "../../services/PriorFederalElectionDateService";
import ElectionDataServiceModule from "../../services/ElectionDataService";
import StateOfficeElectionMenuComponentModule from "../../directives/StateOfficeElectionMenu";

const dialogRootElementId = "tbCandidacyEditorRoot";

const defaultMessageType = "info";
const errorMessageType = "danger";
const successMessageType = "success";
const defaultMessageTimeout = 1000;
const mStatusMessages = new WeakMap();

const mModalInstance = new WeakMap();
const mElectionDataSvc = new WeakMap();
const mElectionDateCalcSvc = new WeakMap();

const mTargetName = new WeakMap();
const mSelectedItem = new WeakMap();
const mSelectedElections = new WeakMap();

class TrackingTargetCandidaciesEditorController {
    constructor($scope,
                $uibModalInstance,
                ElectionDateService,
                ElectionData,
                currentTarget)
    {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($uibModalInstance != null, "Assertion Failure: Missing Modal Instance Dependency");
        
        Assert.StrictAssert(ElectionDateService != null, "Assertion Failure: Missing Prior Federal Election Date Service Dependency");
        Assert.StrictAssert(ElectionData != null, "Assertion Failure: Missing Election Data Service Dependency");
        Assert.StrictAssert(currentTarget != null, "Assertion Failure: Missing Editor Target Resolve Result");

        mModalInstance.set(this, $uibModalInstance);
        mElectionDateCalcSvc.set(this, ElectionDateService);
        mElectionDataSvc.set(this, ElectionData);

        mStatusMessages.set(this, new MessageQueue(defaultMessageType, defaultMessageTimeout));

        Assert.StrictAssert(currentTarget.displayName != null, "Assertion Failure: Missing Display Name For Editor Target");
        mTargetName.set(this, currentTarget.displayName);

        mSelectedItem.set(this, {
            item: null,
            itemType: null
        });

        mSelectedElections.set(this, []);

        $scope.$on("$destroy", function() {
            let candidacyEditorRoot = angular.element(`#${dialogRootElementId}`);
            kendo.destroy(candidacyEditorRoot);
        });

    }
    
    get statusMessages() {
        return mStatusMessages.get(this).messages;
    }

    get targetName() {
        return mTargetName.get(this);
    }

    get selectedElections() {
        return mSelectedElections.get(this);
    }
    
    closeStatusMessage(msgIdx) {
        mStatusMessages.get(this).removeMessageAtIdx(msgIdx);
    }

    updateCheckedItems(updatedItem, updatedItemType) {
        let targetCollection = null;
        let itemId = null;
        switch (updatedItemType) {
            case "Election":
                targetCollection = this.selectedElections;
                itemId = updatedItem.electionId;
                break;
            default:
                return;
        }

        if (updatedItem.checked === true) {
            targetCollection.add(itemId);
        }
        else if (updatedItem.checked === false) {
            targetCollection.delete(itemId);
        }
    }

    changeSelectedItem(selectedMenuItem, selectedMenuItemType) {
        let selectedItem = mSelectedItem.get(this);
        selectedItem.item = selectedMenuItem;
        selectedItem.itemType = selectedMenuItemType;
    }

    selectedMenuItemIsAddableElection() {
        if (mSelectedItem.get(this).itemType === "Election") {
            let selectedElectionId = mSelectedItem.get(this).item.electionId;

            let searchIdx = this.selectedElections.findIndex(
                (el) => (el.electionId === selectedElectionId)
            );
            return (searchIdx === -1);
        }
        return false;
    }

    selectElection() {

        let loadingMessageId = mStatusMessages.get(this).publishMessage(
            "Loading Election Data...",
            MessageQueue.timeoutOverrideValue
        );

        mElectionDataSvc.get(this)
            .getCore({electionId: mSelectedItem.get(this).item.electionId})
            .$promise
            .then(
                (electionItemDataResponse) => {
                    let electionItemData = electionItemDataResponse.result;
                    let selectedElectionDate = electionItemData.electionDate;
                    let cEndDefaultDate = new Date(selectedElectionDate.getUTCFullYear(), selectedElectionDate.getUTCMonth(), selectedElectionDate.getUTCDate());
                    let priorElection = mElectionDateCalcSvc.get(this).getPriorCycleElectionDate(moment(cEndDefaultDate));
                    let cStartDefaultDate = priorElection.add(1, "days").toDate();

                    this.selectedElections.push({
                        electionId: angular.copy(electionItemData.electionId),
                        electionName: angular.copy(electionItemData.displayName),
                        candidacyStart: cStartDefaultDate,
                        candidacyEnd: cEndDefaultDate,
                        candidacyDatePickerOptions: {
                            format: "MMMM d, yyyy",
                            parseFormats: [
                                "MMMM d, yyyy",
                                "MM/dd/yyyy",
                                "M/d/yyyy"
                            ]
                        }
                    });

                    mStatusMessages.get(this).removeMessageById(loadingMessageId);
                    mStatusMessages.get(this).publishMessage("Done.", null, successMessageType);
                },
                (errorMessage) => {
                    mStatusMessages.get(this).removeMessageById(loadingMessageId);
                    mStatusMessages.get(this).publishMessage(errorMessage, MessageQueue.timeoutOverrideValue, errorMessageType);
                }
            );
    }

    removeSelectedElection(selElectionIdx) {
        this.selectedElections.splice(selElectionIdx, 1);
    }

    canAddElections() {
        return (this.selectedElections.length > 0);
    }

    cancel() {
        mModalInstance.get(this).dismiss({
            cancelled: true,
            reason: "User cancelled out of dialog"
        });
    }

    ok() {
        let selectedCandidaciesData = this.selectedElections.map(
            (selElection) => {
                return {
                    electionId: selElection.electionId,
                    candidacyStart: selElection.candidacyStart,
                    candidacyEnd: selElection.candidacyEnd
                };
            }
        );
        mModalInstance.get(this).close(selectedCandidaciesData);
    }
}

TrackingTargetCandidaciesEditorController.$inject = [
    "$scope",
    "$uibModalInstance",
    "ElectionDateService",
    "ElectionData",
    "currentTarget"
];

TrackingTargetCandidaciesEditorController.dependencies = [
    ngAnimate,
    ngAria,
    ngSanitize,
    uiBootstrap,
    kendoDirectivesModule,
    PriorFederalElectionDateServiceModule,
    ElectionDataServiceModule,
    StateOfficeElectionMenuComponentModule
];

TrackingTargetCandidaciesEditorController.template = require("./TrackingTargetCandidaciesEditor.tpl.html");

TrackingTargetCandidaciesEditorController.controllerAs = "tbTargetCandidaciesEditor";

export default TrackingTargetCandidaciesEditorController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-targets-view/TrackingTargetCandidaciesEditorController.js
// module id = T5iE
// module chunks = 1